import { queryOptions } from '@tanstack/react-query';
import { registerBaseURL, registerPartnersBaseURL } from '#constants/hosts';
import { client } from '#queries/utils';
import { common as partnerRegisterCommon } from '#proto/index.qonto.registration.partner_register';
import {
  ApiGetPartnerRegistrationResponse,
  type OrganizationsJsonApiResponse,
  type PartnerRegistrationJsonApiResponse,
  type RegisterPartnersOrganizationsResult,
  type RegisterPartnersRegistrationResult,
  type RegistrationJsonApiResponse,
} from './types';
import { resolveRedirectionTokenOrganization } from './resolve-redirection-token-organization';

const ApiRegistration = partnerRegisterCommon.v1.ApiRegistration;
const ApiOrganization = partnerRegisterCommon.v1.ApiOrganization;
const ApiStakeholder = partnerRegisterCommon.v1.ApiStakeholder;

/**
 * Lists the partner organizations of the current user, with their registration.
 *
 * partner-register only exposes this under `client_api` as JSON:API — there is no
 * protobuf endpoint for it, despite `ListOrganizationsRequest/Response` existing in
 * the proto contract. So we map the JSON:API payload onto the proto types by hand,
 * the same way `registration()` below does.
 *
 * `filter[registration_status_eq]=pending` restricts the list to ongoing partner
 * registrations, matching what the Ember start route queried.
 *
 * `seamless` is an allowlist, not shorthand for "not manual". A manual flow's resume
 * card links to register-partners-js, which sends it straight back here, so a card
 * only belongs on /start if this app can actually carry the flow forward.
 */
export async function listPartnerOrganizations(): Promise<RegisterPartnersOrganizationsResult> {
  const res = await client<OrganizationsJsonApiResponse>(
    `${registerPartnersBaseURL}/client_api/organizations?include=registration&filter[registration_status_eq]=pending&filter[registration_flow_type_eq]=seamless`
  );

  const organizations = res.data.map(entity =>
    ApiOrganization.fromJSON({
      ...entity.attributes,
      id: entity.id,
      // `ApiOrganization.fromJSON` reads `createdAt` in camelCase while every other
      // field — and the API — uses snake_case. Left as is, the date parses to null
      // and the resume screen renders an empty subtitle.
      createdAt: entity.attributes.created_at,
    })
  );

  const organizationIdByRegistrationId = new Map(
    res.data.flatMap(entity => {
      const registrationId = entity.relationships?.registration.data.id;
      return registrationId ? [[registrationId, entity.id] as const] : [];
    })
  );

  const includedRegistrations = (res.included ?? [])
    .filter(item => item.type === 'registrations')
    .map(item =>
      ApiRegistration.fromJSON({
        ...item.attributes,
        id: item.id,
        organization_id: organizationIdByRegistrationId.get(item.id),
      })
    );

  return { organizations, includedRegistrations };
}

export const registerPartnersQueries = {
  all: ['register-partners'] as const,
  lists: () => [...registerPartnersQueries.all, 'list'] as const,
  list: () =>
    queryOptions({
      queryKey: [...registerPartnersQueries.lists()] as const,
      queryFn: listPartnerOrganizations,
    }),
  registration: (id?: string) =>
    queryOptions({
      queryKey: [...registerPartnersQueries.all, 'registration', id] as const,
      queryFn: async (): Promise<RegisterPartnersRegistrationResult> => {
        if (!id) {
          throw new Error('Registration ID is required');
        }

        const res = await client<RegistrationJsonApiResponse>(
          `${registerPartnersBaseURL}/client_api/registrations/${id}?include=organization,stakeholders`
        );

        const registration = ApiRegistration.fromJSON({
          ...res.data.attributes,
          id: res.data.id,
          organization_id: res.data.relationships.organization.data.id,
          stakeholder_ids: res.data.relationships.stakeholders.data.map(s => s.id),
        });

        const includedItems = res.included ?? [];

        const registrationStakeholders = includedItems
          .filter(item => item.type === 'stakeholders')
          .map(item =>
            ApiStakeholder.fromJSON({
              ...item.attributes,
              id: item.id,
              organization_id: res.data.relationships.organization.data.id,
              registration_id: res.data.id,
            })
          );

        const organizationId = res.data.relationships.organization.data.id;
        const organizationData = includedItems.find(
          item => item.type === 'organizations' && item.id === organizationId
        );

        if (!organizationData) {
          throw new Error(`Organization ${organizationId} not found in included resources`);
        }

        // Note: organization from ?include=organization does not populate `features`.
        // The Ember hook fetched features via a separate store.findAll('organization') call.
        // If features are needed, make a separate request to GET /client_api/organizations.
        const organization = ApiOrganization.fromJSON({
          ...organizationData.attributes,
          id: organizationData.id,
        });

        return {
          registration,
          stakeholders: registrationStakeholders,
          organization,
        };
      },
    }),
  partnerRegistration: (id: string) =>
    queryOptions({
      queryKey: [...registerPartnersQueries.all, 'partner-registration', id] as const,
      queryFn: async () => {
        const json = await client<PartnerRegistrationJsonApiResponse>(
          `${registerBaseURL}/v1/partner_registrations/${id}`
        );

        return ApiGetPartnerRegistrationResponse.fromJSON({
          partnerRegistration: {
            ...json.data.attributes,
            id: json.data.id,
          },
        });
      },
    }),
  redirectionTokenOrganization: (tokenId: string) =>
    queryOptions({
      queryKey: [
        ...registerPartnersQueries.all,
        'redirection-token-organization',
        tokenId,
      ] as const,
      staleTime: Infinity,
      retry: false,
      queryFn: () => resolveRedirectionTokenOrganization(tokenId),
    }),
};
